import {
  apiGet,
  apiGetPaginated,
  apiPost,
  apiPostFormData,
  apiUploadFormData,
  apiPatch,
  apiDelete,
  requireApiData,
  ApiError,
} from '@/lib/api/client';
import type {
  ShiftApiRow,
  ListShiftsResult,
  CreateShiftInput,
  UpdateShiftInput,
  ShiftRosterApiRow,
  CreateShiftRosterInput,
  AttendanceRecordApiRow,
  PunchEventApiRow,
  CheckInInput,
  CheckOutInput,
  ListAttendanceParams,
  RegularizationApiRow,
  CreateRegularizationInput,
  ReviewRegularizationInput,
  ListRegularizationsParams,
  AttendanceSettingsApiRow,
  UpdateAttendanceSettingsInput,
  PunchPolicyApiRow,
  PunchPhotoUploadResult,
  GeofenceValidationApiRow,
  ValidateLocationInput,
  RegularizationEvidenceUploadResult,
} from '@/types/attendance';
import type { PaginationMeta } from '@/lib/api/client';
import type { EmployeeApiRecord } from '@/types/employee';

// ──────────────────────────────────────────────────────────────────────────────
// Regularization — API uses lowercase status; normalize nested rows for UI
// ──────────────────────────────────────────────────────────────────────────────

function toIsoString(v: unknown): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString();
  try {
    return new Date(v as string | number).toISOString();
  } catch {
    return null;
  }
}

function titleCaseRegularizationStatus(s: string): RegularizationApiRow['status'] {
  const u = s.toLowerCase();
  if (u === 'approved') return 'Approved';
  if (u === 'rejected') return 'Rejected';
  return 'Pending';
}

/** Profile image URL for regularization rows (HRM photo, directory, then /auth/me like header). */
export function resolveRegularizationEmployeePhotoUrl(
  row: Pick<RegularizationApiRow, 'employeeId' | 'employee'>,
  options?: {
    photoByEmployeeId?: Map<string, string>;
    myEmployee?: EmployeeApiRecord | null;
    /** From GET /auth/me — profileImageUrl */
    authProfileImageUrl?: string | null;
    currentEmployeeId?: string;
  },
): string | null {
  const fromRow = row.employee?.profilePhotoUrl;
  if (typeof fromRow === 'string' && fromRow.trim()) return fromRow.trim();

  const fromDirectory = options?.photoByEmployeeId?.get(row.employeeId);
  if (typeof fromDirectory === 'string' && fromDirectory.trim()) return fromDirectory.trim();

  const isCurrentEmployee =
    Boolean(options?.currentEmployeeId) &&
    row.employeeId === options?.currentEmployeeId;

  if (isCurrentEmployee) {
    const fromAuth = options?.authProfileImageUrl;
    if (typeof fromAuth === 'string' && fromAuth.trim()) return fromAuth.trim();

    const fromMe = options?.myEmployee?.profilePhotoUrl;
    if (typeof fromMe === 'string' && fromMe.trim()) return fromMe.trim();
  }

  const me = options?.myEmployee;
  if (me?.id === row.employeeId) {
    const fromMePhoto = me.profilePhotoUrl;
    if (typeof fromMePhoto === 'string' && fromMePhoto.trim()) return fromMePhoto.trim();
  }

  return null;
}

export function resolveRegularizationEmployeeDesignation(
  row: Pick<RegularizationApiRow, 'employeeId' | 'employee'>,
  options?: {
    designationByEmployeeId?: Map<string, string>;
    myEmployee?: EmployeeApiRecord | null;
  },
): string | null {
  const fromRow = row.employee?.designation;
  if (typeof fromRow === 'string' && fromRow.trim()) return fromRow.trim();

  const fromDirectory = options?.designationByEmployeeId?.get(row.employeeId);
  if (typeof fromDirectory === 'string' && fromDirectory.trim()) return fromDirectory.trim();

  if (options?.myEmployee?.id === row.employeeId) {
    const fromMe = options.myEmployee.designation;
    if (typeof fromMe === 'string' && fromMe.trim()) return fromMe.trim();
  }

  return null;
}

export function normalizeRegularizationRecord(
  row: Record<string, unknown>,
): RegularizationApiRow {
  const r = row as Record<string, any>;
  const emp = r.employee as Record<string, unknown> | undefined | null;
  const att = r.attendance as Record<string, unknown> | undefined | null;
  const shiftRaw =
    (r.assignedShift as Record<string, unknown> | undefined | null) ??
    (att?.shift as Record<string, unknown> | undefined | null);

  const assignedShift = shiftRaw
    ? {
        id: String(shiftRaw.id),
        code: String(shiftRaw.code ?? ''),
        name: String(shiftRaw.name ?? ''),
        startTime: String(shiftRaw.startTime ?? ''),
        endTime: String(shiftRaw.endTime ?? ''),
      }
    : null;

  return {
    id: String(r.id),
    organizationId: String(r.orgId ?? r.organizationId ?? ''),
    employeeId: String(r.employeeId),
    date: toIsoString(r.date) ?? '',
    requestedCheckIn: toIsoString(r.requestedCheckIn),
    requestedCheckOut: toIsoString(r.requestedCheckOut),
    actualCheckIn: toIsoString(r.actualCheckIn ?? att?.checkIn),
    actualCheckOut: toIsoString(r.actualCheckOut ?? att?.checkOut),
    assignedShift,
    unplanned: Boolean(r.unplanned),
    reason: String(r.reason ?? ''),
    evidence: r.evidence,
    status: titleCaseRegularizationStatus(String(r.status ?? 'pending')),
    reviewedBy:
      r.approvedBy != null
        ? String(r.approvedBy)
        : r.reviewedBy != null
          ? String(r.reviewedBy)
          : null,
    reviewedAt: toIsoString(r.approvedAt ?? r.reviewedAt),
    reviewComments:
      r.comments != null
        ? String(r.comments)
        : r.reviewComments != null
          ? String(r.reviewComments)
          : null,
    createdAt: toIsoString(r.createdAt) ?? '',
    updatedAt: toIsoString(r.updatedAt) ?? '',
    employee: emp
      ? {
          firstName: String(emp.firstName ?? ''),
          lastName: String(emp.lastName ?? ''),
          employeeCode:
            emp.employeeId != null ? String(emp.employeeId) : undefined,
          designation:
            typeof emp.designation === 'string' && emp.designation.trim()
              ? emp.designation.trim()
              : null,
          profilePhotoUrl:
            typeof emp.profilePhotoUrl === 'string' && emp.profilePhotoUrl.trim()
              ? emp.profilePhotoUrl.trim()
              : null,
        }
      : undefined,
  };
}

const REGULARIZATION_STATUS_QUERY: Record<string, string> = {
  Pending: 'pending',
  Approved: 'approved',
  Rejected: 'rejected',
};

// ──────────────────────────────────────────────────────────────────────────────
// Shifts
// ──────────────────────────────────────────────────────────────────────────────

export const listShifts = async (): Promise<ListShiftsResult> => {
  const response = await apiGet<ListShiftsResult | ShiftApiRow[]>('/attendance/shifts');
  const data = response.data;

  // Backward-compatible: legacy API returned a bare array
  if (Array.isArray(data)) {
    const shifts = data;
    const active = shifts.filter((s) => s.isActive).length;
    const hoursSum = shifts.reduce((sum, s) => sum + Number(s.workHours ?? 0), 0);
    return {
      shifts,
      summary: {
        total: shifts.length,
        active,
        averageHours:
          shifts.length > 0
            ? Math.round((hoursSum / shifts.length) * 10) / 10
            : 0,
      },
    };
  }

  return requireApiData(data, 'Shifts list was not returned');
};

/** Active shifts only — for roster assignment dropdowns. */
export const listActiveShifts = async (): Promise<ShiftApiRow[]> => {
  const { shifts } = await listShifts();
  return shifts.filter((s) => s.isActive);
};

export const createShift = async (
  input: CreateShiftInput,
): Promise<ShiftApiRow> => {
  const response = await apiPost<ShiftApiRow>(
    '/attendance/shifts',
    input,
  );
  return requireApiData(response.data, 'Shift was not returned after create');
};

export const updateShift = async (
  id: string,
  input: UpdateShiftInput,
): Promise<ShiftApiRow> => {
  const response = await apiPatch<ShiftApiRow>(
    `/attendance/shifts/${id}`,
    input,
  );
  return requireApiData(response.data, 'Shift was not returned after update');
};

export const deleteShift = async (id: string): Promise<void> => {
  await apiDelete(`/attendance/shifts/${id}`);
};

// ──────────────────────────────────────────────────────────────────────────────
// Shift Rosters
// ──────────────────────────────────────────────────────────────────────────────

export const ensureShiftRosters = async (params?: {
  months?: number;
  force?: boolean;
}): Promise<{
  created: number;
  extended: number;
  skipped: number;
  employees: number;
  defaultShiftId: string | null;
  cached?: boolean;
}> => {
  const queryParams = new URLSearchParams();
  if (params?.months != null) queryParams.set('months', String(params.months));
  if (params?.force) queryParams.set('force', 'true');
  const qs = queryParams.toString();
  const { data } = await apiGet<{
    created: number;
    extended: number;
    skipped: number;
    employees: number;
    defaultShiftId: string | null;
    cached?: boolean;
  }>(`/attendance/rosters/ensure${qs ? `?${qs}` : ''}`);
  return requireApiData(data, 'Failed to ensure shift rosters');
};

function rosterRangeFromMonths(startDateIso: string | undefined, months: number): {
  startIso: string;
  endIso: string;
} {
  const start = startDateIso
    ? new Date(startDateIso.includes('T') ? startDateIso : `${startDateIso}T12:00:00`)
    : new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setMonth(end.getMonth() + months);
  end.setHours(23, 59, 59, 999);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

async function bulkCreateRostersViaSingleAssign(input: {
  shiftId: string;
  months: number;
  startDate?: string;
  employeeIds?: string[];
}): Promise<{
  created: number;
  failed: number;
  total: number;
  months: number;
  startDate: string;
  endDate: string;
}> {
  const { listEmployees } = await import('@/services/employees.service');
  const { startIso, endIso } = rosterRangeFromMonths(input.startDate, input.months);
  let employeeIds = input.employeeIds;
  if (!employeeIds?.length) {
    const res = await listEmployees({ page: 1, limit: 500, status: 'ACTIVE' });
    employeeIds = res.data.map((e) => e.id);
  }
  let created = 0;
  let failed = 0;
  for (const employeeId of employeeIds) {
    try {
      const payload: CreateShiftRosterInput = {
        employeeId,
        shiftId: input.shiftId,
        startDate: startIso,
        endDate: endIso,
        isRecurring: false,
      };
      await createRoster(payload);
      created += 1;
    } catch {
      failed += 1;
    }
  }
  return {
    created,
    failed,
    total: employeeIds.length,
    months: input.months,
    startDate: startIso,
    endDate: endIso,
  };
}

export const bulkCreateRosters = async (input: {
  shiftId: string;
  months: number;
  startDate?: string;
  employeeIds?: string[];
}): Promise<{
  created: number;
  failed: number;
  total: number;
  months: number;
  startDate: string;
  endDate: string;
}> => {
  try {
    const response = await apiPost<{
      created: number;
      failed: number;
      total: number;
      months: number;
      startDate: string;
      endDate: string;
    }>('/attendance/rosters/bulk', input);
    return requireApiData(response.data, 'Bulk roster create failed');
  } catch (err) {
    const notFound =
      err instanceof ApiError &&
      (err.status === 404 || /route.*not found/i.test(err.message));
    if (!notFound) throw err;
    return bulkCreateRostersViaSingleAssign(input);
  }
};

export const listRosters = async (params?: {
  employeeId?: string;
  shiftId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
  /** Ensures a forward baseline roster exists for unassigned active employees. */
  ensureDefaults?: boolean;
  months?: number;
}): Promise<ShiftRosterApiRow[]> => {
  const queryParams = new URLSearchParams();
  queryParams.set('page', String(params?.page ?? 1));
  queryParams.set('limit', String(params?.limit ?? 500));
  if (params?.employeeId) queryParams.set('employeeId', params.employeeId);
  if (params?.shiftId) queryParams.set('shiftId', params.shiftId);
  if (params?.startDate) queryParams.set('startDate', params.startDate);
  if (params?.endDate) queryParams.set('endDate', params.endDate);
  if (params?.ensureDefaults) queryParams.set('ensureDefaults', 'true');
  if (params?.ensureDefaults && params?.months != null) {
    queryParams.set('months', String(params.months));
  }
  const { data } = await apiGetPaginated<ShiftRosterApiRow>(
    `/attendance/rosters?${queryParams.toString()}`,
  );
  return data;
};

export const createRoster = async (
  input: CreateShiftRosterInput,
): Promise<ShiftRosterApiRow> => {
  const response = await apiPost<ShiftRosterApiRow>(
    '/attendance/rosters',
    input,
  );
  return requireApiData(response.data, 'Roster was not returned after create');
};

// ──────────────────────────────────────────────────────────────────────────────
// Attendance Records
// ──────────────────────────────────────────────────────────────────────────────

function normalizePunchEvent(ev: Record<string, unknown>): PunchEventApiRow {
  const row = ev as Record<string, any>;
  const atRaw = row.at;
  const at =
    typeof atRaw === 'string'
      ? atRaw
      : atRaw instanceof Date
        ? atRaw.toISOString()
        : atRaw != null
          ? new Date(atRaw as string | number).toISOString()
          : '';
  return {
    type: row.type === 'out' ? 'out' : 'in',
    at,
    location: row.location ?? undefined,
    coordinates: row.coordinates,
    address: row.address ?? undefined,
    ipAddress: row.ipAddress ?? row.ip_address ?? undefined,
    ipLocation: row.ipLocation ?? row.ip_location ?? undefined,
    workMode: row.workMode,
    photoKey: row.photoKey ?? row.photo_key ?? undefined,
    photoUrl: row.photoUrl ?? row.photo_url ?? undefined,
    punchSource: row.punchSource ?? row.punch_source,
    notes: row.notes,
  };
}

/** Map Prisma HRM rows to UI-friendly attendance records. */
export function normalizeAttendanceRecord(
  row: Record<string, unknown>,
): AttendanceRecordApiRow {
  const r = row as Record<string, any>;
  const otHours = Number(r.overtimeHours ?? 0);
  const emp = r.employee as Record<string, any> | undefined;
  const dept = emp?.department as { id?: string; code?: string; name?: string } | null | undefined;
  const departmentName =
    dept?.name ?? (r.departmentName as string | null | undefined) ?? null;
  const departmentId =
    (emp?.departmentId as string | undefined) ??
    (r.departmentId as string | undefined) ??
    dept?.id ??
    null;

  return {
    id: r.id != null && r.id !== '' ? String(r.id) : '',
    organizationId: String(r.orgId ?? r.organizationId ?? ''),
    employeeId: r.employeeId != null ? String(r.employeeId) : '',
    departmentName,
    departmentId,
    date: toIsoString(r.date) ?? new Date().toISOString(),
    checkIn: r.checkIn ?? null,
    checkOut: r.checkOut ?? null,
    checkInLocation: r.checkInLocation ?? null,
    checkOutLocation: r.checkOutLocation ?? null,
    checkInCoordinates: r.checkInCoordinates,
    checkOutCoordinates: r.checkOutCoordinates,
    workHours: r.workHours ?? null,
    status: (r.status as AttendanceRecordApiRow['status']) || 'Present',
    isOvertime: otHours > 0,
    overtimeMinutes: otHours > 0 ? Math.round(otHours * 60) : null,
    shiftId: r.shiftId ?? null,
    remarks: r.notes ?? r.remarks ?? null,
    regularizationId: r.regularizationId ?? null,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    employee: emp
      ? {
          firstName: emp.firstName,
          lastName: emp.lastName,
          employeeCode: emp.employeeId,
          designation: emp.designation ?? null,
          departmentId: departmentId ?? undefined,
          department: departmentName
            ? {
                id: dept?.id,
                code: dept?.code,
                name: departmentName,
              }
            : null,
        }
      : undefined,
    shift: r.shift ?? r.assignedShift,
    punchEvents: Array.isArray(r.punchEvents)
      ? r.punchEvents.map((ev: Record<string, unknown>) => normalizePunchEvent(ev))
      : undefined,
    canPunchIn: r.canPunchIn as boolean | undefined,
    canPunchOut: r.canPunchOut as boolean | undefined,
    dayClosed: r.dayClosed as boolean | undefined,
    isPunchedIn: r.isPunchedIn as boolean | undefined,
    openPunchInAt: r.openPunchInAt != null ? String(r.openPunchInAt) : null,
    shiftStart: r.shiftStart != null ? String(r.shiftStart) : null,
    shiftEnd: r.shiftEnd != null ? String(r.shiftEnd) : null,
  };
}

export const listAttendance = async (params: ListAttendanceParams) => {
  const queryParams = new URLSearchParams();
  queryParams.set('page', String(params.page ?? 1));
  queryParams.set('limit', String(params.limit ?? 500));
  if (params.employeeId) queryParams.set('employeeId', params.employeeId);
  const from = params.startDate ?? params.from;
  const to = params.endDate ?? params.to;
  if (from) {
    queryParams.set('from', from);
    queryParams.set('startDate', from);
  }
  if (to) {
    queryParams.set('to', to);
    queryParams.set('endDate', to);
  }
  if (params.status) queryParams.set('status', params.status);
  if (params.departmentId) queryParams.set('departmentId', params.departmentId);
  if (params.scope) queryParams.set('scope', params.scope);

  const result = await apiGetPaginated<Record<string, unknown>>(
    `/attendance/records?${queryParams.toString()}`,
  );
  return {
    ...result,
    data: result.data.map(normalizeAttendanceRecord),
  };
};

export const getMyTodayAttendance =
  async (): Promise<AttendanceRecordApiRow | null> => {
    const response = await apiGet<Record<string, unknown> | null>(
      '/attendance/me/today',
    );
    if (!response.data || typeof response.data !== 'object') return null;
    return normalizeAttendanceRecord(response.data);
  };

export const getMyAttendanceForDate = async (
  dateOnly: string,
): Promise<AttendanceRecordApiRow | null> => {
  const response = await apiGet<Record<string, unknown> | null>(
    `/attendance/me/day?date=${encodeURIComponent(dateOnly)}`,
  );
  if (!response.data || typeof response.data !== 'object') return null;
  return normalizeAttendanceRecord(response.data);
};

async function postPunch(
  path: '/attendance/check-in' | '/attendance/check-out',
  input: CheckInInput | CheckOutInput,
): Promise<AttendanceRecordApiRow> {
  const { getOrFetchClientPublicIp } = await import('@/lib/auth/clientPublicIp');
  const headers: Record<string, string> = {};
  const pubIp = await getOrFetchClientPublicIp();
  if (pubIp) headers['X-Client-Public-IP'] = pubIp;

  const response = await apiPost<Record<string, unknown>>(path, input, headers);
  return normalizeAttendanceRecord(
    requireApiData(response.data, 'Punch record was not returned'),
  );
}

export const checkIn = async (input: CheckInInput): Promise<AttendanceRecordApiRow> =>
  postPunch('/attendance/check-in', input);

export const checkOut = async (input: CheckOutInput): Promise<AttendanceRecordApiRow> =>
  postPunch('/attendance/check-out', input);

// ──────────────────────────────────────────────────────────────────────────────
// Regularization
// ──────────────────────────────────────────────────────────────────────────────

export const listRegularizations = async (
  params: ListRegularizationsParams,
): Promise<{ data: RegularizationApiRow[]; pagination: PaginationMeta }> => {
  const queryParams = new URLSearchParams();
  if (params.page) queryParams.set('page', String(params.page));
  if (params.limit) queryParams.set('limit', String(params.limit));
  if (params.employeeId) queryParams.set('employeeId', params.employeeId);
  if (params.status) {
    const mapped =
      REGULARIZATION_STATUS_QUERY[params.status] ?? params.status.toLowerCase();
    queryParams.set('status', mapped);
  }
  if (params.startDate) queryParams.set('startDate', params.startDate);
  if (params.endDate) queryParams.set('endDate', params.endDate);
  if (params.scope) queryParams.set('scope', params.scope);

  const result = await apiGetPaginated<Record<string, unknown>>(
    `/attendance/regularizations?${queryParams.toString()}`,
  );
  return {
    ...result,
    data: result.data.map((row) => normalizeRegularizationRecord(row)),
  };
};

export const uploadRegularizationEvidence = async (
  files: File[],
): Promise<RegularizationEvidenceUploadResult> => {
  const form = new FormData();
  for (const file of files) {
    form.append('attachments', file, file.name);
  }
  const response = await apiPostFormData<RegularizationEvidenceUploadResult>(
    '/attendance/regularizations/evidence',
    form,
  );
  return requireApiData(response.data, 'Evidence upload returned no data');
};

export const appendRegularizationEvidence = async (
  regularizationId: string,
  files: File[],
): Promise<RegularizationApiRow> => {
  const form = new FormData();
  for (const file of files) {
    form.append('attachments', file, file.name);
  }
  const response = await apiUploadFormData<Record<string, unknown>>(
    `/attendance/regularizations/${regularizationId}/evidence`,
    form,
  );
  return normalizeRegularizationRecord(
    requireApiData(response.data, 'Regularization was not returned after upload') as Record<
      string,
      unknown
    >,
  );
};

export const createRegularization = async (
  input: CreateRegularizationInput,
): Promise<RegularizationApiRow> => {
  const response = await apiPost<Record<string, unknown>>(
    '/attendance/regularizations',
    input,
  );
  return normalizeRegularizationRecord(
    requireApiData(
      response.data,
      'Regularization was not returned after create',
    ) as Record<string, unknown>,
  );
};

export const reviewRegularization = async (
  id: string,
  input: ReviewRegularizationInput,
): Promise<RegularizationApiRow> => {
  const response = await apiPatch<Record<string, unknown>>(
    `/attendance/regularizations/${id}/review`,
    input,
  );
  return normalizeRegularizationRecord(
    requireApiData(
      response.data,
      'Regularization was not returned after review',
    ) as Record<string, unknown>,
  );
};

// ──────────────────────────────────────────────────────────────────────────────
// Attendance Settings
// ──────────────────────────────────────────────────────────────────────────────

export const getPunchPolicy = async (): Promise<PunchPolicyApiRow> => {
  const response = await apiGet<PunchPolicyApiRow>('/attendance/punch-policy');
  return requireApiData(response.data, 'Punch policy not returned');
};

export const validatePunchLocation = async (
  input: ValidateLocationInput,
): Promise<GeofenceValidationApiRow> => {
  const response = await apiPost<GeofenceValidationApiRow>(
    '/attendance/validate-location',
    input,
  );
  return requireApiData(response.data, 'Location validation not returned');
};

export const getPunchPhotoViewUrl = async (photoKey: string): Promise<string> => {
  const response = await apiGet<{ url: string }>(
    `/attendance/punch-photo-url?key=${encodeURIComponent(photoKey)}`,
  );
  const data = requireApiData(response.data, 'Punch photo URL not returned');
  if (!data.url?.trim()) {
    throw new ApiError('Punch photo URL not returned', 500);
  }
  return data.url.trim();
};

export const uploadPunchPhoto = async (
  file: Blob,
  punchType: 'in' | 'out',
): Promise<PunchPhotoUploadResult> => {
  const form = new FormData();
  form.append('punch_photo', file, 'punch-selfie.jpg');
  const response = await apiPostFormData<PunchPhotoUploadResult>(
    `/attendance/punch-photo?type=${punchType}`,
    form,
  );
  return requireApiData(response.data, 'Punch photo upload returned no data');
};

export const getAttendanceSettings =
  async (): Promise<AttendanceSettingsApiRow> => {
    const response = await apiGet<AttendanceSettingsApiRow>(
      '/attendance/settings',
    );
    return requireApiData(response.data, 'Attendance settings not returned');
  };

export const updateAttendanceSettings = async (
  input: UpdateAttendanceSettingsInput,
): Promise<AttendanceSettingsApiRow> => {
  const response = await apiPatch<AttendanceSettingsApiRow>(
    '/attendance/settings',
    input,
  );
  return requireApiData(
    response.data,
    'Attendance settings not returned after update',
  );
};
